import React, { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "react-hot-toast";
import { SectionTitle, InputCss } from "../../_parts.js";
import { uploadFileTemporary } from "../../../../api/borrowerPortal/uploadFile.js";
import BorrowerSteppedForm from "../shared/BorrowerSteppedForm.jsx";
import FormField, { FormFieldRow } from "../shared/FormField.jsx";
import FormUploadsPanel from "../shared/FormUploadsPanel.jsx";
import FormReviewPanel from "../shared/FormReviewPanel.jsx";
import { getDraftStorageKey, loadFormDraft } from "../shared/useBorrowerFormDraft.js";
import { buildUploadLabelMap } from "../shared/formValidation.js";
import PhoneField from "../../../../components/ui/PhoneField.jsx";
import { isValidPhone, PHONE_ERROR_MOBILE } from "../../../../utils/validatePhone.js";

const uid = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

const soloFormSchema = z.object({
  firstName: z.string().min(1, "First Name is required"),
  surname: z.string().min(1, "Surname is required"),
  phone: z.string().min(1, PHONE_ERROR_MOBILE).refine((v) => isValidPhone(v), { message: PHONE_ERROR_MOBILE }),
  email: z.string().email("Invalid email address"),
  credit: z.enum(["Good", "Bad", "Poor"], {
    errorMap: () => ({ message: "Please select credit history" }),
  }),
  props: z.coerce.number().min(0, "Must be 0 or more"),
  propertyAddress: z.string().min(1, "Security Address is required"),
});

const DETAILS_FIELDS = ["firstName", "surname", "phone", "email", "credit"];
const PROPERTY_FIELDS = ["props", "propertyAddress"];

export default function SoloForm({
  onBack,
  onNext,
  applicationName,
  initialPayload,
  mode = "wizard",
  productId,
  isSubmitting = false,
}) {
  const savedDraft = loadFormDraft(getDraftStorageKey("solo", productId || initialPayload?.productId));
  const initial = savedDraft?.btl?.solo || initialPayload?.btl?.solo || {};
  const [uploads, setUploads] = useState(initial.uploads || initialPayload?.btl?.solo_uploads || {});
  const [uploadingFiles, setUploadingFiles] = useState({});

  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors, touchedFields },
  } = useForm({
    resolver: zodResolver(soloFormSchema),
    mode: "onBlur",
    defaultValues: {
      firstName: initial.firstName || "",
      surname: initial.surname || "",
      phone: initial.phone || "",
      email: initial.email || "",
      credit: initial.credit || "",
      props: initial.props ?? "",
      propertyAddress: initial.propertyAddress || "",
    },
  });

  const currentValues = watch();
  const draftKey = getDraftStorageKey("solo", productId || initialPayload?.productId);

  const constructPayload = (formValues, currentUploads, extras = {}) => {
    const updatedFormData = {
      ...(initialPayload?.formData || {}),
      firstName: formValues.firstName,
      surname: formValues.surname,
      phone: formValues.phone,
      email: formValues.email,
      credit: formValues.credit,
      props: formValues.props,
      propertyAddress: formValues.propertyAddress,
      first_name: formValues.firstName,
      last_name: formValues.surname,
      phone_number: formValues.phone,
      credit_history: formValues.credit,
      number_of_properties_owned: formValues.props,
      property_address: formValues.propertyAddress,
      ...extras,
    };

    return {
      ...(initialPayload || {}),
      formData: updatedFormData,
      btl: {
        ...(initialPayload?.btl || {}),
        borrowerType: "Solo",
        solo: {
          firstName: formValues.firstName,
          surname: formValues.surname,
          phone: formValues.phone,
          email: formValues.email,
          credit: formValues.credit,
          props: formValues.props,
          propertyAddress: formValues.propertyAddress,
        },
        solo_uploads: currentUploads,
      },
    };
  };

  const draftSnapshot = useMemo(
    () => constructPayload(currentValues, uploads),
    [currentValues, uploads, initialPayload]
  );

  const stepErrors = useMemo(
    () => ({
      details: DETAILS_FIELDS.some((f) => errors[f]),
      property: PROPERTY_FIELDS.some((f) => errors[f]),
      documents: false,
      review: false,
    }),
    [errors]
  );

  const validateStep = async (stepIndex) => {
    if (stepIndex === 0) return trigger(DETAILS_FIELDS);
    if (stepIndex === 1) return trigger(PROPERTY_FIELDS);
    return true;
  };

  const handleUpload = (bucketKey, fieldname) => async (files) => {
    if (!files?.length) return;
    const nextBucket = [...(uploads[bucketKey] || [])];

    for (const file of files) {
      const uploadId = uid();
      const fileName = file.name || "Unknown file";
      setUploadingFiles((prev) => ({
        ...prev,
        [bucketKey]: [...(prev[bucketKey] || []), { id: uploadId, name: fileName, progress: 0 }],
      }));

      try {
        const up = await uploadFileTemporary({
          file,
          isPrivate: true,
          onProgress: (percent) => {
            if (typeof percent === "number" && percent < 100) {
              setUploadingFiles((prev) => ({
                ...prev,
                [bucketKey]: (prev[bucketKey] || []).map((u) =>
                  u.id === uploadId ? { ...u, progress: percent } : u
                ),
              }));
            }
          },
        });
        await new Promise((r) => setTimeout(r, 200));
        nextBucket.push({
          id: uid(),
          file_url: up?.file_url,
          file_name: up?.file_name,
          name: up?.name,
          _temp: true,
          _fieldname: fieldname,
        });
      } catch (error) {
        console.error("Upload error:", error);
        toast.error(`Failed to upload ${fileName}`);
      } finally {
        setUploadingFiles((prev) => ({
          ...prev,
          [bucketKey]: (prev[bucketKey] || []).filter((u) => u.id !== uploadId),
        }));
      }
    }

    setUploads({ ...uploads, [bucketKey]: nextBucket });
  };

  const handleRemove = (bucketKey) => (fileId) => {
    const nextBucket = (uploads[bucketKey] || []).filter(
      (f) => f.id !== fileId && f.name !== fileId
    );
    setUploads({ ...uploads, [bucketKey]: nextBucket });
  };

  const finish = (extras = {}) => {
    const payload = constructPayload(currentValues, uploads, extras);
    onNext?.({
      type: "solo",
      firstName: currentValues.firstName,
      surname: currentValues.surname,
      phone: currentValues.phone,
      email: currentValues.email,
      credit: currentValues.credit,
      props: currentValues.props,
      propertyAddress: currentValues.propertyAddress,
      uploads,
      payloadObj: payload,
      submitFromReview: true,
      agree1: extras.agree1,
      agree2: extras.agree2,
    });
  };

  const uploadLabels = useMemo(() => buildUploadLabelMap(), []);

  return (
    <>
      <BorrowerSteppedForm
        title="SOLE TRADER REGISTRATION FORM"
        subtitle="Please complete all required fields and upload necessary documents"
        draftKey={draftKey}
        draftSnapshot={draftSnapshot}
        stepErrors={stepErrors}
        validateStep={validateStep}
        onOuterBack={onBack}
        mode={mode}
        isSubmitting={isSubmitting}
        detailsContinueDisabled={!isValidPhone(currentValues.phone)}
        renderDetails={() => (
          <div>
            <SectionTitle>Personal Information</SectionTitle>
            <FormFieldRow>
              <FormField
                id="solo-firstName"
                label="First Name"
                required
                error={errors.firstName?.message}
                touched={touchedFields.firstName}
              >
                <input
                  id="solo-firstName"
                  className={`input w-full ${errors.firstName ? "border-rose-300" : ""}`}
                  aria-invalid={!!errors.firstName}
                  aria-describedby={errors.firstName ? "solo-firstName-error" : undefined}
                  {...register("firstName")}
                />
              </FormField>
              <FormField
                id="solo-surname"
                label="Surname"
                required
                error={errors.surname?.message}
                touched={touchedFields.surname}
              >
                <input
                  id="solo-surname"
                  className={`input w-full ${errors.surname ? "border-rose-300" : ""}`}
                  {...register("surname")}
                />
              </FormField>
              <FormField id="solo-phone" label="Phone Number" required>
                <PhoneField
                  id="solo-phone"
                  value={watch("phone")}
                  onChange={(next) => setValue("phone", next, { shouldValidate: true, shouldTouch: true })}
                  onBlur={() => trigger("phone")}
                />
              </FormField>
              <FormField
                id="solo-email"
                label="Email"
                required
                error={errors.email?.message}
                touched={touchedFields.email}
              >
                <input
                  id="solo-email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  className={`input w-full ${errors.email ? "border-rose-300" : ""}`}
                  {...register("email")}
                />
              </FormField>
            </FormFieldRow>

            <div className="mt-4">
              <FormField
                id="solo-credit"
                label="Credit History"
                required
                error={errors.credit?.message}
                touched={touchedFields.credit}
              >
                <div className="flex flex-wrap gap-3" role="radiogroup" aria-label="Credit History">
                  {["Good", "Bad", "Poor"].map((opt) => (
                    <label
                      key={opt}
                      className={`px-4 py-2 rounded-full border cursor-pointer ${
                        currentValues.credit === opt
                          ? "bg-sky-400 text-white border-sky-400"
                          : "bg-white text-slate-700 border-slate-200"
                      }`}
                    >
                      <input type="radio" value={opt} className="hidden" {...register("credit")} />
                      {opt}
                    </label>
                  ))}
                </div>
              </FormField>
            </div>
          </div>
        )}
        renderProperty={() => (
          <div>
            <SectionTitle>Property &amp; finance</SectionTitle>
            <FormFieldRow>
              <FormField
                id="solo-props"
                label="Number of properties owned"
                required
                error={errors.props?.message}
                touched={touchedFields.props}
              >
                <input
                  id="solo-props"
                  type="number"
                  inputMode="numeric"
                  min="0"
                  className={`input w-full ${errors.props ? "border-rose-300" : ""}`}
                  {...register("props")}
                />
              </FormField>
              <FormField
                id="solo-propertyAddress"
                label="Security Address"
                required
                error={errors.propertyAddress?.message}
                touched={touchedFields.propertyAddress}
                className="sm:col-span-2"
              >
                <input
                  id="solo-propertyAddress"
                  className={`input w-full ${errors.propertyAddress ? "border-rose-300" : ""}`}
                  {...register("propertyAddress")}
                />
              </FormField>
            </FormFieldRow>
          </div>
        )}
        renderDocuments={() => (
          <FormUploadsPanel
            uploads={uploads}
            uploadingFiles={uploadingFiles}
            onUpload={handleUpload}
            onRemove={handleRemove}
          />
        )}
        renderReview={({ goToStep, onFooterStateChange }) => (
          <FormReviewPanel
            hideSubmitButton
            onFooterStateChange={onFooterStateChange}
            sections={[
              {
                stepId: "details",
                stepIndex: 0,
                title: "Your details",
                rows: [
                  { label: "First Name", value: currentValues.firstName },
                  { label: "Surname", value: currentValues.surname },
                  { label: "Phone", value: currentValues.phone },
                  { label: "Email", value: currentValues.email },
                  { label: "Credit History", value: currentValues.credit },
                ],
              },
              {
                stepId: "property",
                stepIndex: 1,
                title: "Property & finance",
                rows: [
                  { label: "Properties owned", value: currentValues.props },
                  { label: "Security Address", value: currentValues.propertyAddress },
                ],
              },
            ]}
            uploads={uploads}
            uploadLabels={uploadLabels}
            onEditStep={goToStep}
            isSubmitting={isSubmitting}
            onSubmit={({ agree1, agree2 }) => finish({ agree1, agree2 })}
          />
        )}
      />
      <InputCss />
    </>
  );
}
